import { FunctionComponent } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './PainScale.module.css';

interface PainScaleProps {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  variant?: 'numeric' | 'emoji' | 'vas';
  required?: boolean;
  helperText?: string;
  error?: string;
  hasNextButton?: boolean;
  onNext?: () => void;
}

const PainScale: FunctionComponent<PainScaleProps> = ({
  id,
  label,
  value,
  onChange,
  variant = 'numeric',
  required = false,
  helperText,
  error,
  hasNextButton = false,
  onNext
}) => {
  const painLevels = [
    { value: 0, label: 'No Pain', emoji: '😊', color: '#00a859' },
    { value: 1, label: 'Minimal', emoji: '🙂', color: '#28a745' },
    { value: 2, label: 'Mild', emoji: '😐', color: '#6bc644' },
    { value: 3, label: 'Uncomfortable', emoji: '😕', color: '#a3d143' },
    { value: 4, label: 'Moderate', emoji: '😣', color: '#dbdc42' },
    { value: 5, label: 'Distracting', emoji: '😖', color: '#ffc107' },
    { value: 6, label: 'Distressing', emoji: '😫', color: '#ff9800' },
    { value: 7, label: 'Intense', emoji: '😰', color: '#ff6b35' },
    { value: 8, label: 'Severe', emoji: '😭', color: '#ff5722' },
    { value: 9, label: 'Excruciating', emoji: '😱', color: '#f44336' },
    { value: 10, label: 'Unbearable', emoji: '🤯', color: '#dc3545' }
  ];

  const currentLevel = painLevels[value];

  if (variant === 'vas') {
    return (
      <div className={styles.container}>
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
        <div className={styles.vasContainer}>
          <input
            id={id}
            type="range"
            min="0"
            max="10"
            step="1"
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className={styles.vasSlider}
            style={{
              background: `linear-gradient(to right, #00a859 0%, #ffc107 50%, #dc3545 100%)`
            }}
            required={required}
          />
          <div className={styles.vasLabels}>
            <span>No Pain</span>
            <span>Moderate</span>
            <span>Worst Pain</span>
          </div>
          <div className={styles.vasValue} style={{ color: currentLevel.color }}>
            {value} - {currentLevel.label}
          </div>
        </div>
        {helperText && <p className={styles.helperText}>{helperText}</p>}
        {error && <p className={styles.errorText}>{error}</p>}
        
        {hasNextButton && (
          <button 
            type="button" 
            className={styles.nextButton} 
            onClick={onNext}
          >
            Next
            <ChevronRightIcon />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>
      
      <div className={styles.scaleContainer}>
        {painLevels.map((level) => (
          <button
            key={level.value}
            type="button"
            className={`${styles.scaleButton} ${value === level.value ? styles.selected : ''}`}
            onClick={() => onChange(level.value)}
            style={{
              borderColor: value === level.value ? level.color : undefined,
              backgroundColor: value === level.value ? `${level.color}15` : undefined
            }}
          >
            {variant === 'emoji' && (
              <span className={styles.emoji}>{level.emoji}</span>
            )}
            <span className={styles.number} style={{ color: value === level.value ? level.color : undefined }}>
              {level.value}
            </span>
            {variant === 'emoji' && (
              <span className={styles.levelLabel}>{level.label}</span>
            )}
          </button>
        ))}
      </div>

      {variant === 'numeric' && (
        <div className={styles.currentLevel} style={{ color: currentLevel.color }}>
          Current: {currentLevel.label}
        </div>
      )}

      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {error && <p className={styles.errorText}>{error}</p>}
      
      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

export default PainScale;